import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, ClipboardCheck, Clock, Headset, ShieldCheck, ShoppingCart, Wallet, Wrench } from 'lucide-react';
import { api } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';

const TOTP_APPROVER_EMAILS = ['admin@senerfi.com', 'soporteti@senerfi.com', 'joluguma22@gmail.com'];

export default function MisPendientesWidget({ onNavigate, onNavigateRoute, modulePrefs }) {
  const { currentUser } = useUserPrefs();
  const [loading, setLoading] = useState(true);
  const [preopCount, setPreopCount] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [pendingTickets, setPendingTickets] = useState(0);
  const [pendingExpenses, setPendingExpenses] = useState(0);
  const [pendingTotp, setPendingTotp] = useState(0);
  const [pendingOvertimeBoss, setPendingOvertimeBoss] = useState(0);
  const [pendingOvertimeFinal, setPendingOvertimeFinal] = useState(0);
  const [pendingNoveltiesBoss, setPendingNoveltiesBoss] = useState(0);
  const [pendingNoveltiesHr, setPendingNoveltiesHr] = useState(0);
  const [pendingExits, setPendingExits] = useState(0);
  const [failed, setFailed] = useState(false);

  const canSeeSeguridad = !!modulePrefs?.seguridad;
  const isTotpApprover = TOTP_APPROVER_EMAILS.includes(String(currentUser?.email || '').trim().toLowerCase());

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [preopRes, purchaseRes, ticketsRes, expenseRes, totpRes, overtimeRes, exitsRes] = await Promise.all([
          api.preoperationals.pendingAuthorization().catch(() => null),
          api.purchaseRequests.myPending().catch(() => null),
          currentUser?.id ? api.admin.supports.list({ viewer_admin_id: currentUser.id, limit: 500 }).catch(() => null) : Promise.resolve(null),
          api.accounting.expenseRequests.myPending().catch(() => null),
          isTotpApprover ? api.auth.totpAdmin.list().catch(() => null) : Promise.resolve(null),
          api.accounting.overtimeExtras.myPending().catch(() => null),
          canSeeSeguridad ? api.maintenanceServiceOrders.pendingSeguridad().catch(() => null) : Promise.resolve(null),
        ]);
        if (!alive) return;
        const preopItems = Array.isArray(preopRes?.data) ? preopRes.data : [];
        setPreopCount(preopItems.length);
        setPendingRequests(Number(purchaseRes?.pending_requests || 0));
        setPendingOrders(Number(purchaseRes?.pending_orders || 0));
        const ticketItems = Array.isArray(ticketsRes?.data) ? ticketsRes.data : [];
        setPendingTickets(ticketItems.filter((t) => String(t?.status || '') !== 'Cerrado').length);
        setPendingExpenses(Number(expenseRes?.pending_expenses || 0));
        const totpItems = Array.isArray(totpRes?.data) ? totpRes.data : [];
        setPendingTotp(totpItems.length);
        setPendingOvertimeBoss(Number(overtimeRes?.pending_as_boss || 0));
        setPendingOvertimeFinal(Number(overtimeRes?.pending_as_final_approver || 0));
        setPendingNoveltiesBoss(Number(overtimeRes?.pending_novelties_as_boss || 0));
        setPendingNoveltiesHr(Number(overtimeRes?.pending_novelties_as_hr || 0));
        const exitItems = Array.isArray(exitsRes?.data) ? exitsRes.data : [];
        setPendingExits(exitItems.length);
      } catch (_) {
        if (alive) setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [currentUser?.id, canSeeSeguridad, isTotpApprover]);

  if (loading || failed) return null;

  const totalPending = preopCount + pendingRequests + pendingOrders + pendingTickets
    + pendingExpenses + pendingTotp + pendingOvertimeBoss + pendingOvertimeFinal
    + pendingNoveltiesBoss + pendingNoveltiesHr + pendingExits;

  if (totalPending === 0) {
    return (
      <div className="mb-4 flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 p-3.5 text-sm text-green-700">
        <CheckCircle2 size={18} />
        Estás al día: no tienes pendientes por resolver.
      </div>
    );
  }

  const items = [
    preopCount > 0 && {
      key: 'transportes',
      label: `Preoperacional${preopCount === 1 ? '' : 'es'} del día por firmar`,
      count: preopCount,
      icon: ClipboardCheck,
    },
    pendingExits > 0 && {
      key: 'seguridad',
      label: `Salida${pendingExits === 1 ? '' : 's'} de taller por autorizar`,
      count: pendingExits,
      icon: Wrench,
    },
    pendingRequests > 0 && {
      key: 'contabilidad',
      label: `Solicitud${pendingRequests === 1 ? '' : 'es'} de compra por aprobar`,
      count: pendingRequests,
      icon: ShoppingCart,
    },
    pendingOrders > 0 && {
      key: 'contabilidad',
      label: `Orden${pendingOrders === 1 ? '' : 'es'} de compra por aprobar`,
      count: pendingOrders,
      icon: ShoppingCart,
    },
    pendingExpenses > 0 && {
      key: 'contabilidad',
      label: `Solicitud${pendingExpenses === 1 ? '' : 'es'} de gastos por aprobar`,
      count: pendingExpenses,
      icon: Wallet,
    },
    pendingOvertimeBoss > 0 && {
      key: 'recursos-humanos',
      label: `Hora${pendingOvertimeBoss === 1 ? '' : 's'} extra por validar (jefe inmediato)`,
      count: pendingOvertimeBoss,
      icon: Clock,
    },
    pendingOvertimeFinal > 0 && {
      key: 'contabilidad',
      label: `Hora${pendingOvertimeFinal === 1 ? '' : 's'} extra por aprobación final`,
      count: pendingOvertimeFinal,
      icon: Clock,
    },
    pendingNoveltiesBoss > 0 && {
      key: 'recursos-humanos',
      label: `Novedad${pendingNoveltiesBoss === 1 ? '' : 'es'} por aprobar (jefe inmediato)`,
      count: pendingNoveltiesBoss,
      icon: Clock,
    },
    pendingNoveltiesHr > 0 && {
      key: 'recursos-humanos',
      label: `Novedad${pendingNoveltiesHr === 1 ? '' : 'es'} por recibir (Gestión Humana)`,
      count: pendingNoveltiesHr,
      icon: Clock,
    },
    pendingTickets > 0 && {
      key: 'administracion-ti',
      label: `Ticket${pendingTickets === 1 ? '' : 's'} de mesa de ayuda asignado${pendingTickets === 1 ? '' : 's'}`,
      count: pendingTickets,
      icon: Headset,
      onClick: () => {
        try { localStorage.setItem('senerfi_ui_admin_ti_view', 'soportes'); } catch (_) {}
        try { localStorage.setItem('senerfi_ui_admin_ti_group', 'gestion'); } catch (_) {}
      },
    },
    pendingTotp > 0 && {
      label: `Activación${pendingTotp === 1 ? '' : 'es'} de doble factor por aprobar`,
      count: pendingTotp,
      icon: ShieldCheck,
      route: '/aprobaciones-2fa',
    },
  ].filter(Boolean);

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
      <div className="mb-3 flex items-center gap-2">
        <AlertCircle size={18} className="text-amber-600" />
        <span className="text-sm font-semibold text-gray-800">
          Tienes {totalPending} pendiente{totalPending === 1 ? '' : 's'} por resolver
        </span>
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => {
          const Icon = it.icon;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => {
                it.onClick && it.onClick();
                if (it.route) { onNavigateRoute && onNavigateRoute(it.route); }
                else { onNavigate && onNavigate(it.key); }
              }}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-white bg-white px-3 py-2 text-left text-sm shadow-sm hover:bg-gray-50"
            >
              <span className="flex items-center gap-2 text-gray-700">
                <Icon size={16} className="text-gray-500" />
                {it.label}
              </span>
              <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-bold text-red-700">
                {it.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
